import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const normalizeOptions = (options = []) => {
    const flat = [];
    options.forEach((entry) => {
        if (entry?.group && Array.isArray(entry.options)) {
            flat.push({ type: 'group', label: entry.group });
            entry.options.forEach((opt) => {
                flat.push({ type: 'option', ...opt });
            });
            return;
        }
        flat.push({ type: 'option', ...entry });
    });
    return flat;
};

const findLabel = (options, value) => {
    for (const entry of options) {
        if (entry?.group && Array.isArray(entry.options)) {
            const match = entry.options.find(
                (opt) => String(opt.value) === String(value),
            );
            if (match) return match.label;
            continue;
        }
        if (String(entry.value) === String(value)) return entry.label;
    }
    return '';
};

const ThemedSelect = ({
    value,
    onChange,
    options = [],
    name,
    id,
    disabled = false,
    placeholder = 'Select…',
    size = 'default',
    className = '',
    menuClassName = '',
    align = 'left',
}) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const listId = useId();

    const flatOptions = useMemo(() => normalizeOptions(options), [options]);
    const selectableOptions = flatOptions.filter(
        (entry) => entry.type === 'option' && !entry.disabled,
    );
    const selectedLabel = findLabel(options, value);
    const displayLabel = selectedLabel || placeholder;

    const updateMenuPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const gap = 6;
        const viewportPadding = 8;
        const menuWidth = Math.max(rect.width, size === 'compact' ? 144 : rect.width);
        let left = align === 'right' ? rect.right - menuWidth : rect.left;
        left = Math.min(
            Math.max(viewportPadding, left),
            window.innerWidth - menuWidth - viewportPadding,
        );

        setMenuStyle({
            position: 'fixed',
            top: rect.bottom + gap,
            left,
            width: menuWidth,
            zIndex: 9999,
        });
    };

    useLayoutEffect(() => {
        if (!open) return undefined;
        updateMenuPosition();

        const onReposition = () => updateMenuPosition();
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [open, align, size]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            const target = event.target;
            if (
                rootRef.current?.contains(target) ||
                menuRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const emitChange = (nextValue) => {
        onChange?.({
            target: {
                name: name || undefined,
                value: nextValue,
            },
        });
        setOpen(false);
    };

    const triggerClass =
        size === 'compact'
            ? 'inline-flex min-h-[28px] items-center gap-1.5 rounded-md px-1 py-0.5 text-[11px] font-semibold text-zinc-800 outline-none transition hover:text-teal-800 focus-visible:ring-2 focus-visible:ring-teal-600/30 dark:text-zinc-200 dark:hover:text-teal-400'
            : 'cpg-input flex w-full cursor-pointer items-center justify-between gap-2 text-left';

    const menuSizeClass =
        size === 'compact' ? 'text-[11px]' : 'text-sm';
    const optionPaddingClass =
        size === 'compact' ? 'px-2.5 py-1.5' : 'px-3 py-2';

    const menu =
        open && menuStyle ? (
            <ul
                ref={menuRef}
                id={listId}
                role="listbox"
                style={{ ...menuStyle, maxHeight: '16rem', overflowY: 'auto' }}
                className={`overflow-x-hidden rounded-xl border border-zinc-200 bg-white py-1 shadow-lg shadow-zinc-900/10 dark:border-zinc-700 dark:bg-zinc-900 dark:shadow-black/40 ${menuSizeClass} ${menuClassName}`}
            >
                {flatOptions.map((entry, index) => {
                        if (entry.type === 'group') {
                            return (
                                <li
                                    key={`group-${entry.label}-${index}`}
                                    className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500"
                                    aria-hidden
                                >
                                    {entry.label}
                                </li>
                            );
                        }

                        const isSelected = String(entry.value) === String(value);
                        const isDisabled = Boolean(entry.disabled);

                        return (
                            <li key={`${entry.value}-${index}`} role="presentation">
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    disabled={isDisabled}
                                    onClick={() => {
                                        if (!isDisabled) emitChange(entry.value);
                                    }}
                                    className={`flex w-full items-center justify-between gap-2 text-left transition ${optionPaddingClass} ${
                                        isDisabled
                                            ? 'cursor-not-allowed text-zinc-400 dark:text-zinc-600'
                                            : isSelected
                                              ? 'bg-teal-50 font-semibold text-teal-800 dark:bg-teal-950/50 dark:text-teal-300'
                                              : 'text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800/80'
                                    }`}
                                >
                                    <span className="truncate">{entry.label}</span>
                                    {isSelected ? (
                                        <i
                                            className="fa-solid fa-check text-[10px] text-teal-600 dark:text-teal-400"
                                            aria-hidden
                                        />
                                    ) : null}
                                </button>
                            </li>
                        );
                    })}
                    {selectableOptions.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-zinc-400 dark:text-zinc-500">
                            No options
                        </li>
                    ) : null}
            </ul>
        ) : null;

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            <button
                ref={triggerRef}
                type="button"
                id={id}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => {
                    if (!disabled) setOpen((prev) => !prev);
                }}
                className={`${triggerClass} ${
                    disabled ? 'cursor-not-allowed opacity-60' : ''
                } ${!selectedLabel && size !== 'compact' ? 'text-zinc-400 dark:text-zinc-500' : ''}`}
            >
                <span className="truncate">{displayLabel}</span>
                <i
                    className={`fa-solid fa-chevron-down shrink-0 text-[10px] text-zinc-400 transition-transform dark:text-zinc-500 ${
                        open ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                />
            </button>

            {menu ? createPortal(menu, document.body) : null}
        </div>
    );
};

export default ThemedSelect;
